import Vue from 'vue';
import { Portal } from '@linusborg/vue-simple-portal';
import transition from '../../mixins/transition';

export default {
  name: 'Dialog',
  components: {
    Portal,
  },
  mixins: [transition],
  inheritAttrs: false,
  model: {
    prop: 'value',
    event: 'change',
  },
  props: {
    value: Boolean,
    seamless: Boolean,
    maximized: Boolean,
    fullWidth: Boolean,
    fitContent: Boolean,
    top: Boolean,
    bottom: Boolean,
    left: Boolean,
    right: Boolean,
    persistent: Boolean,
    outsideClose: {
      type: Boolean,
      default: false,
    },
    noEscDismiss: Boolean,
    noBackdropDismiss: Boolean,
    containerStyle: {
      type: [Object, String, Array],
      required: false,
      default: '',
    },
    selector: {
      type: String,
      required: false,
      default: undefined,
    },
    prepend: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      dialog: false,
      modal: false,
      mouseDownTarget: null,
      mouseUpTarget: null,
    };
  },
  computed: {
    modalBackdrop() {
      const isBackdropDisabled = this.seamless || this.maximized;
      return !isBackdropDisabled && this.dialog;
    },
    canClose() {
      return this.dialog && !(this.persistent || this.noBackdropDismiss);
    },
    canOutsideClose() {
      return this.dialog && this.outsideClose && !this.persistent;
    },
  },
  watch: {
    value(value) {
      value ? this.openModal() : this.closeDialog();
    },
    modal(value) {
      this.$emit('change', value);
    },
  },
  created() {
    // Create global stack of modals
    const modals = Vue.observable({ openedModals: [] });
    this.$root.modals = this.$root.modals || modals;
  },
  mounted() {
    this.value ? this.openModal() : this.closeDialog();

    window.addEventListener('mousedown', this.onMouseDown);
    window.addEventListener('mouseup', this.onMouseUp);
  },
  beforeDestroy() {
    window.removeEventListener('mouseup', this.onMouseUp);
    window.removeEventListener('mousedown', this.onMouseDown);

    this.deleteModalFromGlobalStack();
  },
  methods: {
    hide() {
      this.$emit('hide');
      this.closeModal();
    },
    beforeHide() {
      this.$emit('before-hide');
      this.deleteModalFromGlobalStack();
    },
    show() {
      this.$emit('show');
    },
    beforeShow() {
      this.$emit('before-show');
    },
    closeLastDialog() {
      const currentModal = this._uid;
      const openedModals = this.$root.modals.openedModals;
      const lastOpenedModal = openedModals[openedModals.length - 1];

      if (lastOpenedModal === currentModal) {
        this.$emit('escape-key');
        this.closeDialog();
      }
    },
    onKeyDown(event) {
      if (event.key === 'Escape') {
        const isEscEnabled = !(this.persistent || this.noEscDismiss);
        if (isEscEnabled) {
          this.closeLastDialog();
        }
      }
    },
    closeOutside() {
      if (!this.canOutsideClose) return;

      const modal = this.$refs.modal;
      const slotContent = modal && modal.$children[0];
      const slotContentEl = slotContent && slotContent.$el;

      if (
        slotContentEl &&
        !slotContentEl.contains(this.mouseDownTarget) &&
        !slotContentEl.contains(this.mouseUpTarget)
      ) {
        this.closeDialog();
      }
    },
    onMouseDown(event) {
      this.mouseDownTarget = event.target;
    },
    onMouseUp(event) {
      this.mouseUpTarget = event.target;
      this.closeOutside();
    },
    closeHandler() {
      if (this.canClose) {
        this.closeDialog();
      }
    },
    openModal() {
      this.modal = true;
      this.$root.modals.openedModals.push(this._uid);
      this.openDialog();

      window.addEventListener('keydown', this.onKeyDown);
    },
    closeModal() {
      this.modal = false;
      this.deleteModalFromGlobalStack();

      window.removeEventListener('keydown', this.onKeyDown);
    },
    deleteModalFromGlobalStack() {
      this.$root.modals.openedModals = this.$root.modals.openedModals.filter((id) => {
        return this._uid !== id;
      });
    },
    openDialog() {
      this.dialog = true;
    },
    closeDialog() {
      this.dialog = false;
    },
  },
};
